export type Entity = {
  id: string
  type: string
  timestamp?: number
}

export type DeletableEntity = Entity & {
  deleted?: boolean
}

export type Etag = Entity & {
  type: 'Etag'
  entityType: string
  timestamp: number
}

export type EntityDict<T extends { id: string }> = { [id: string]: T }

export const domainsOfExcellence = {
  InfectionPrevention: 'Infection prevention',
  PatientExperienceAndSatisfaction: 'Patient experience and satisfaction',
  PatientSafety: 'Patient safety',
  EfficientResourceUtilization: 'Efficient resource utilization',
  MedicationSafety: 'Medication safety',
  SafeSurgery: 'Safe surgery'
} as const

export type DomainOfExcellence = keyof typeof domainsOfExcellence

export type ScoringDomainOfExcellence = DomainOfExcellence | 'None'

export const inviteStatuses = {
  Invited: 'Invited',
  Accepted: 'Accepted',
  Revoked: 'Revoked',
  Expired: 'Expired'
} as const

export type InviteStatus = keyof typeof inviteStatuses

export type UserOrganizationDetail = {
  organizationId: string
  inviteStatus?: InviteStatus
  permissionGroupId?: string | null
}

export type UserSlim = Entity & {
  email?: string
  displayName?: string
  color: string
  type: 'UserSlim'
  userId: string
  search: string
}

export function userSlimId(userId: string) {
  return `UserSlim_${userId}`
}

export function toUserSlim(user: Pick<User, 'id' | 'email' | 'displayName' | 'color'>): UserSlim {
  return {
    id: userSlimId(user.id),
    type: 'UserSlim',
    userId: user.id,
    email: user.email,
    displayName: user.displayName,
    color: user.color,
    search: `${user.displayName}|${user.email}`.toLocaleLowerCase()
  }
}

export type UserListItem = UserSlim & {
  lastLoginDate?: string
  userOrganizationDetails: UserOrganizationDetail[]
}

export type User = Entity & {
  email: string
  displayName?: string
  color: string
  language: string
  role: string
  country: string
  isOsso: boolean
  hasRunTutorial: boolean
  hasRunCollabTutorial: boolean
  healthStreamId?: string
  permissionGroupIds?: string[]
  organizationIds: string[]
  organizationPermissions?: OrganizationPermissionGroup[]
  type: 'User'
}

export type OrganizationPermissionGroup = {
  organizationId: string
  permissionGroupIds: string[]
}

export const buildPlatforms = ['linux', 'quest', 'windows'] as const

export type BuildPlatform = (typeof buildPlatforms)[number]

export type Platform = 'web' | BuildPlatform

export type Course = Entity & {
  name: string
  linkedModuleBuildsId: string
  techniqueId: string
  type: 'course'
  healthStreamCourseId?: string
}

export type CourseOrganization = DeletableEntity & {
  type: 'CourseOrganization'
  courseId: string
  organizationId: string
}

export function courseOrganizationId(courseId: string, organizationId: string) {
  return `${courseId}_${organizationId}`
}

export type Assignment = Entity & {
  type: 'Assignment'
  organizationId: string
  name: string
  courseId: string
  startDate: string
  dueDate: string
  instructions?: string
  createdById: string
}

export type AssignmentListItem = Entity & {
  type: 'AssignmentListItem'
  organizationId: string
  name: string
  courseId: string
  dueDate: string
  assigneeCount: number
  completedCount: number
}

export type CreateAssignment = {
  id: string
  name: string
  courseId: string
  dueDate: string
  instructions?: string
}

export type UpdateAssignment = {
  name: string
  dueDate: string
  instructions?: string
}

export type UserAssignmentStatus = DeletableEntity & {
  type: 'UserAssignmentStatus'
  assignmentId: string
  userId: string
  organizationId: string
  status: string
  attempts: number
  completedDate?: string
  bestScore?: number
  lastAttempt?: string
}

export const UserAssignmentStatus = {
  NotStarted: 'NotStarted',
  InProgress: 'InProgress',
  Completed: 'Completed'
} as const

export type Stage = {
  id?: string
  criticalActions: CriticalAction[]
  name: string
}

export type CriticalAction = {
  id: string
  description: string
  rationale?: string
  rules?: ScoringRule[]
  criticalSubActions?: CriticalSubAction[]
  domainsOfExcellence?: DomainOfExcellence[]
}

export type CriticalSubAction = {
  id: string
  description: string
  supplySelectionItems?: SupplySelectionItem[]
}

export type SupplySelectionItem = {
  id: string
  name: string
  correct: boolean
}

export type Flow = {
  id?: string
  name: string
  stages: FlowStage[]
}

export type FlowStage = {
  id: string
  name: string
  lanes: FlowLane[]
}

export type FlowLane = {
  id: string
  name: string
  earlyEntry: boolean
  advanceStage: boolean
  nodes: FlowNode[]
}

export type FlowNode = {
  id: string
  name: string
  type: string
  activityType: string
  gateLogic: string
  gateEvents: string[]
}

export type ScoringRubric = {
  moduleId: string
  criticalActions: ScoringCriticalAction[]
}

export type EvaluationTrigger = {
  type: string
  id: string
}

export type AssessmentFieldKind = 'single' | 'multi' | 'numeric'

export type AssessmentOption = {
  id: string
  label: string
}

export type AssessmentField = {
  id: string
  label: string
  kind: AssessmentFieldKind
  options?: AssessmentOption[]
  unit?: string
  min?: number
  max?: number
  step?: number
  prefilled?: boolean
  notGraded?: boolean
}

export type AssessmentTab = {
  id: string
  label: string
  fields: AssessmentField[]
}

export type Assessment = {
  id: string
  label: string
  criticalActionIds: string[]
  tabs: AssessmentTab[]
}

export type ScoringRule = {
  id?: string
  optionSelected?: { fieldId: string; optionIds: string[] }
  valueEntered?: { fieldId: string; value: number }
  activityStarted?: { activityId: string }
  activityCompleted?: { activityId: string }
  laneStarted?: { laneId: string }
  laneCompleted?: { laneId: string }
  placed?: { sourceId?: string; targetId?: string }
  attached?: { sourceId?: string; targetId?: string }
  not?: ScoringRule
  rules?: ScoringRule[]
  minPass?: number
}

export type ScoringCriticalAction = {
  id: string
  displayName: string
  domainsOfExcellence?: ScoringDomainOfExcellence[]
  rationale: string
  stageId: string
  rules?: ScoringRule[]
  minPass?: number
  triggers?: EvaluationTrigger[]
  startTriggers?: EvaluationTrigger[]
  autoPlayCorrectAudio?: boolean
  children?: ScoringCriticalAction[]
}

export type RunPerformance = {
  id: string
  date: string
  score: number
  isTest: boolean
  criticalActions: CriticalActionEvent[]
}

export type CriticalActionEvent = { id: string; passed: boolean }

export type PlayerHintEvent = { id: string; criticalAction?: string }

export type SupplySelectionEvent = {
  id: string
  itemsPlaced: string[]
}

export type CriticalSubActionEvent = {
  id: string
  passed: boolean
  parent: string
}

export type PlayerRun = {
  id: string
  userId?: string
  date?: string
  techniqueId: string
  linkedPlayerBuildsId?: string
  criticalActions: CriticalActionEvent[]
  hints: PlayerHintEvent[]
  supplySelections: SupplySelectionEvent[]
  criticalSubActions: CriticalSubActionEvent[]
  score: number
  baseScore: number
  passed: boolean
  isTest: boolean
  completed: boolean
  time: number
  hintsUsed: number
  failedContaminationCriticalActionCount: number
  courseId?: string
  moduleBuildId?: string
  linkedModuleBuildsId?: string
  multiplayerSessionId?: string
  exitModule?: boolean
  sceneModule?: { flow: Flow; rubric: ScoringRubric }
  criticalActionResults?: ScoringCriticalActionResult[]
}

export type ResultStatus = 'correct' | 'incorrect' | 'incomplete' | 'extra'

export type CriticalActionDetailSection = SuppliesDetailSection | SubActionsDetailSection

export type SuppliesDetailSection = { kind: 'supplies' | 'extraSupplies'; items: SupplySelectionResult[] }

export type SubActionsDetailSection = { kind: 'subActions'; items: SubActionResult[] }

export type SupplyQuantity = {
  name: string
  count: number
}

export type SupplySelectionResult = {
  id: string
  status: ResultStatus
  expected: SupplyQuantity[]
  placed: SupplyQuantity[]
}

export type SubActionResult = {
  id: string
  status: ResultStatus
  description: string
}

export type FieldStatus = 'correct' | 'incorrect' | 'notCharted'

export type FieldValue = { optionLabels: string[] } | { value: number; unit?: string }

export type AssessmentFieldResult = {
  id: string
  label: string
  status: FieldStatus
  charted?: FieldValue
  expected?: FieldValue
}

export type AssessmentTabResult = {
  id: string
  label: string
  fields: AssessmentFieldResult[]
}

export type AssessmentResult = {
  label: string
  tabs: AssessmentTabResult[]
}

export type CriticalActionResult = {
  criticalAction: CriticalAction
  criticalActionDetails?: CriticalActionDetailSection[]
  assessment?: AssessmentResult
  assessmentField?: AssessmentFieldResult
  passed: boolean
  hints: PlayerHintEvent[]
}

export type StageResult = {
  title: string
  criticalActionResults: CriticalActionResult[]
  hints: PlayerHintEvent[]
  passed: boolean
}

export type RunResult = {
  id: string
  score: number
  baseScore: number
  passed: boolean
  isTest: boolean
  completed: boolean
  time: number
  hintsUsed: number
  failedContaminationCriticalActionCount: number
  courseId?: string
  multiplayerSessionId?: string
  exitModule?: boolean
  stageResults: StageResult[]
  passedCriticalActions: number
  totalCriticalActions: number
  formattedDuration: string
  techniqueId?: string
}

export type ScoringResult = 'NotAttempted' | 'Failed' | 'Passed'

export type RuleResult = {
  ruleId: string
  outcome: ScoringResult
  actual?: { optionIds?: string[]; value?: number }
}

export type ScoringCriticalActionResult = {
  id: string
  result: ScoringResult
  ruleResults?: RuleResult[]
}

export type ScoringHintResult = { timestamp: number; criticalActionId?: string }

export type ScoringRunResult = {
  passed: boolean
  score: number
  baseScore: number
  hintsUsed: number
  hints?: ScoringHintResult[]
  time: number
  criticalActionResults: ScoringCriticalActionResult[]
}

export type SceneModule = {
  techniqueId: string
  techniqueName: string
  techniquePath: string
  flow?: Flow
  rubric?: ScoringRubric
  assessments?: Assessment[]
}

export type UserCoursePerformance = Entity & {
  organizationId: string
  courseId: string
  userId: string
  recentRuns: RunPerformance[]
  bestRun: RunPerformance
  previousBestRun?: RunPerformance
  totalRunCount: number
  courseCompletedDate?: string
  type: 'userCoursePerformance'
}

export type GitInfo = {
  sha: string
  branch: string
  username: string
  repo: string
}

export type EmbeddedModule = {
  techniqueId: string
  techniquePath: string
  gitInfo: GitInfo
  stages?: Stage[]
  flow?: Flow
  rubric?: ScoringRubric
  rubricNotCompatible?: boolean
  rubricErrors?: string[]
  assessments?: Assessment[]
}

export type ModuleBuild = {
  id: string
  uri: string
}

export type LinkedModuleBuilds = Entity & {
  type: 'LinkedModuleBuilds'
  moduleSha: string
  coreVRSha: string
  ossoSharedSha: string
  techniqueId: string
  techniquePath: string
  description: string
  clientVersion: string
  unityVersion: string
  date: string
  stages?: Stage[]
  flow?: Flow
  rubric?: ScoringRubric
  rubricNotCompatible?: boolean
  rubricErrors?: string[]
  assessments?: Assessment[]
  ossoSharedGitInfo?: GitInfo
  moduleGitInfo?: GitInfo
  coreVRGitInfo?: GitInfo
  questBuild?: ModuleBuild
  windowsBuild?: ModuleBuild
  linuxBuild?: ModuleBuild
  isArchived: boolean
}

export type PlayerBuild = {
  id: string
  uri: string
  size: number
}

export type LinkedPlayerBuilds = Entity & {
  type: 'LinkedPlayerBuilds'
  date: string
  description: string
  version: string
  username: string
  embeddedModules: EmbeddedModule[]
  playerGitInfo?: GitInfo
  coreVRGitInfo?: GitInfo
  ossoSharedGitInfo?: GitInfo
  questBuild?: PlayerBuild
  windowsBuild?: PlayerBuild
  linuxBuild?: PlayerBuild
  legacyWindowsEmbedded?: boolean
}

export type LinkedPlayerBuildsSettings = Entity & {
  type: 'LinkedPlayerBuildsSettings'
  isArchived: boolean
  isSubmittedToMeta: boolean
  notes?: string
  metaUrl?: string
}

export type LinkedPlayerBuildsListItem = Entity & {
  type: 'LinkedPlayerBuildsListItem'
  date: string
  version: string
  description: string
  username: string
  platforms: BuildPlatform[]
  isArchived: boolean
  isSubmittedToMeta: boolean
  notes?: string
}

export type TechniqueCategory = Entity & {
  title: string
  type: 'TechniqueCategory'
}

export type SurveyQuestion = {
  id: string
  title: string
  answers: string[]
  start?: string
  end?: string
}

export type Survey = {
  definitionId: string
  questions: SurveyQuestion[]
  cadence: number[]
  hideSkipButton: boolean
}

export type SurveyAnswers = { [questionId: string]: string }

export function userCoursePerformanceId(organizationId: string, courseId: string, userId: string) {
  return `UserCoursePerformance_${organizationId}_${courseId}_${userId}`
}

export function isRunPerformanceForRun(runPerformance: RunPerformance | undefined, runLogId: string) {
  return runPerformance?.id.startsWith(`${runLogId}_`) ?? false
}

export function performanceIncludesRun(performance: UserCoursePerformance | undefined, runLogId: string) {
  return performance?.recentRuns.some(x => isRunPerformanceForRun(x, runLogId)) ?? false
}

export function arrayToEntityDict<T extends Entity>(array: T[]): EntityDict<T> {
  return array.reduce((acc: EntityDict<T>, x: T) => {
    acc[x?.id] = x
    return acc
  }, {} as EntityDict<T>)
}

export function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined
}
